import { Component, Input, EventEmitter, Output, SimpleChanges, ViewChild, ElementRef, HostBinding, HostListener, TemplateRef, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, Injector, OnInit } from '@angular/core';
import { SettingsService } from 'services/settings.service';
import { LoggingService } from 'services/logging.service';
import { InputPairingService } from 'services/input-pairing.service';
import { Subject, takeUntil } from 'rxjs';
import { getEffectiveDecimalSeparator, parseFraction, roundToPrecision } from '@cf-shared/shared-util';
import { MeasurementType, MeasureUnitConfig, GlobalMeasurementTypes, GlobalMeasurementConfigs, Config_None, Config_Number } from '@cf-shared/measure-configs';
import { Formatting, canonValueToTexts_global, dualUnitRatio, findBestUnitConfig, shortUnitName, translateMeasureDescription } from 'util/formatting';
import { isE2ETest } from 'environments/environment-helpers';
import { KeyboardOptions, KeyCode, VirtualKeyboardService } from 'services/virtual-keyboard.service';
import { findNextVisibleSequenceElement } from 'util/input-sequence';
import { animationFrame, DestroyGuard } from 'services/browser-utils';
import { describeError } from '@cf-shared/error-describe';
import { InputValue } from 'components/virtual-keyboard/virtual-keyboard';
import { PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { LL } from 'i18n/i18n.service';

// Convert what the user sees into the value we store.
//
// A measurement is stored in one canonical unit (grams for a mass, metres for a
// length), but shown in whatever unit the user picked -- and some units need two
// numbers side by side, like 5 feet 9 inches or 13 stone 4 pounds. unitValue1 and
// unitValue2 say how many canonical units one of each display unit is worth, and
// nominalUnit is the scale the measurement type itself is written in.
//
// A field left empty or holding something unreadable contributes nothing, which
// is why each term is added only when it is a finite number.
function displayValuesToCanonical(
  value1: number | undefined,
  value2: number | undefined,
  unitValue1: number,
  unitValue2: number | undefined,
  nominalUnit: number,
): number {
  let canonical = 0;
  if (isFinite(value1)) {
    canonical += (value1 * unitValue1) / nominalUnit;
  }
  if (unitValue2 != undefined && isFinite(value2)) {
    canonical += (value2 * unitValue2) / nominalUnit;
  }
  return canonical;
}

// Pick the entry of a roller's value list that sits nearest the given number.
//
// A roller can only stop on one of the values it was given, so a number that
// came from somewhere else -- what the field already showed, a suggestion, the
// measurement type's default -- has to be pulled onto the nearest one before the
// wheel can start there. Ties keep the earlier entry.
function closestValueTo(values: InputValue[], target: number): InputValue {
  return values.reduce((closest, candidate) =>
    Math.abs(candidate.numeric - target) < Math.abs(closest.numeric - target) ? candidate : closest
  );
}

// Stand-in for a type id missing from GlobalMeasurementTypes: one unitless number,
// never converted or clamped. 'number' and 'integer' are not reused because both
// clamp to 0..99 at whole-number precision, which would silently cut a value down.
export const UNKNOWN_MEASUREMENT_TYPE: MeasurementType = {
  id: 'unknown',
  nominalUnit: 1,
  minimum: -Infinity,
  maximum: Infinity,
  precision: 0.01,
  configs: [Config_Number],
};

// whether a damaged unitsPrefsJSON has been reported in this page load (see loadPrefs)
let unitsPrefsDamageReported = false;

@Component({
  selector: 'cs-input-measure',
  templateUrl: 'cs-input-measure.html',
  host: { 'class': 'input-measure' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsInputMeasureComponent implements OnInit, OnDestroy {
  private destroyGuard = new DestroyGuard();

  @Input() useCustomReadonlyLayout: boolean = false;

  @Input() canonValue: number = 0;
  @Output() canonValueChange = new EventEmitter<number>();

  @Input() userUnit: string = '';
  @Output() userUnitChange = new EventEmitter<string>();
  @Input() public type: string;
  @Input() public category: string = '';
  // * The component will pass all events through in that case.
  @Input() public readonly: boolean = false;
  @Input() public disabled: boolean = false;  // Disabled state - grayed out and no interaction
  public notEditable: boolean = false;  // this is used to disable the input field if the selected unit is "none"
  // Shows "~" and rounds the value. Only valid when `readonly` is set to true.
  @Input() public makeBallpark: boolean = false;
  // If set, the component will only allow selection from the given custom units, instead of letting the user choose from all units in the given type.
  // This is used for units like "cups chopped", "large eggs", "tablespoons", which are not standard units, but make sense in some contexts.
  @Input() public customUnitConfigs: MeasureUnitConfig[];
  @Input() public lockUnit: boolean = false;
  @Input() public label?: TranslatedString;
  /** text shown in the primary field while it is empty. When not set, the field shows `--` (or `--.-`, or `/ `) as before. */
  @Input() public placeholder?: TranslatedString;

  @ViewChild('input1') input1: ElementRef;
  @ViewChild('display1') display1: ElementRef;
  @ViewChild('display2') display2: ElementRef;
  @Input() public autofocus: boolean;
  @Input() public emitDoneEventOnEveryKeypress = false;
  @Input() public preselectValues: number[] = undefined;
  @Input() public suggestedPreselectValue: number = undefined;
  @Input() public forceKeypadInput: boolean = false;
  @Input() public unitChangeConvertsValue = true;
  @Input() public showUnit: boolean = true;
  @Input() public precisionOverride: number;

  // Pairing properties for dual roller mode
  @Input() public pairedWith: CsInputMeasureComponent; // Reference to paired component
  @Input() public pairRole: 'primary' | 'secondary'; // Role in the pair

  // Observable for component destruction
  public destroyed$ = new Subject<void>();

  // this is fired when user leaves the control, needed to save values to db etc
  @Output() editDone = new EventEmitter<number>();

  // Set the readonly attribute on the host element when readonly property is true
  @HostBinding('attr.readonly') get readonlyAttr() {
    return this.readonly ? '' : null;
  }

  // Set the disabled attribute on the host element when disabled property is true
  @HostBinding('attr.disabled') get disabledAttr() {
    return this.disabled ? '' : null;
  }

  // Add dynamic classes to the host element
  @HostBinding('class.scrolled') get scrolledClass() {
    return this.useInputScroller();
  }

  @HostBinding('class.dual-unit') get dualUnitClass() {
    return this.unit2name != null;
  }

  // Handle focusin event on the host element
  @HostListener('focusin', ['$event'])
  onHostFocusIn(event: FocusEvent) {
    void this.onFocusIn(event);
  }

  @ViewChild('inputValuePopover') inputValuePopover: TemplateRef<any>;

  measurementType: MeasurementType;
  measurementUnitConfigs: MeasureUnitConfig[];
  measurementUnitConfig: MeasureUnitConfig;

  multipleUnits: boolean;
  valueLen: 'XXS' | 'XS' | 'S' | 'M' | 'L' | '';
  value2Len: 'XXS' | 'XS' | 'S' | 'M' | 'L' | '';
  maximumChars: number | undefined;

  nominalUnit: number;
  precision: number = 1;
  fractionDenominatorMax: number | null = null;

  text1: string;
  unit1name: string;
  unit1short: string;

  text2: string;
  unit2name: string;

  // this is only used for non-interactive mode - dumps all into one text for more compact layout
  fullText: string;

  // "internal" values (in contrast those on the API surface) are used to keep the display stable and prevent infinite refresh loops
  // Perhaps we "might" make it work without some of those, but having this completely separated
  // increases safety against corner case problems causing infinite loops.
  canonValue_int: number = 0;
  userUnit_int: string = '';
  string1_int: string = '';
  string2_int: string = '';

  currentInputControl: HTMLElement = undefined;

  // Cached mobile detection - set once on init
  isMobile: boolean = false;

  // Dynamically calculate if this input is currently active (keyboard is open for it)
  // Source of truth is the keyboard service - only one input can be active at a time
  // For dual rollers, show active when either primary or secondary is focused
  // For paired components in roller mode, show active when either component is focused
  @HostBinding('class.active') get isActive() {
    const targetEl = this.keyboardService.currentTargetElement;
    if (!targetEl) return false;

    // Check if the keyboard's target is one of our elements (primary or secondary)
    if (targetEl === this.display1?.nativeElement ||
        targetEl === this.input1?.nativeElement ||
        targetEl === this.display2?.nativeElement) {
      return true;
    }

    // if paired and in roller mode, also check paired component's elements
    // (in keypad mode, only the focused input should be blue)
    if (this.pairedWith && !this.keyboardService.isKeypadMode) {
      if (targetEl === this.pairedWith.display1?.nativeElement ||
          targetEl === this.pairedWith.input1?.nativeElement ||
          targetEl === this.pairedWith.display2?.nativeElement) {
        return true;
      }
    }

    return false;
  }

  // Check if a specific element should show selection highlight
  // Goes directly to service - no intermediate state
  // Note: template ref vars give HTMLElement, not ElementRef
  isShowingSelection(element: HTMLElement): boolean {
    const svc = this.keyboardService;
    return svc.currentTargetElement === element
      && svc.isValueSelected
      && svc.isKeypadMode;
  }

  // Check if a specific element should show blinking cursor
  // Goes directly to service - no intermediate state
  // Note: template ref vars give HTMLElement, not ElementRef
  isShowingCursor(element: HTMLElement): boolean {
    const svc = this.keyboardService;
    return svc.currentTargetElement === element
      && svc.isKeypadMode
      && !svc.isValueSelected;
  }

  // the text an empty primary field shows. Both the desktop input and the mobile display read it,
  // so a caller's placeholder shows at every width
  get primaryPlaceholder(): string {
    if (this.placeholder) {
      return this.placeholder;
    }
    return this.measurementType?.forceDecimals ? '--.-' : (this.notEditable ? '/ ' : '--');
  }

  constructor(
    private settings: SettingsService,
    public elementRef: ElementRef,
    private popupMenu: PopupMenuProvider,
    private keyboardService: VirtualKeyboardService,
    public cdr: ChangeDetectorRef,
    private injector: Injector,
    private pairingService: InputPairingService,
  ) {

  }

  ngOnInit(): void {
    // Cache mobile detection once on init
    this.isMobile = this.isTouchDevice();

    this.initIfNeeded();

    // Register pair if this is the primary component
    if (this.pairedWith && this.pairRole === 'primary') {
      this.pairingService.registerPair(this, this.pairedWith);
    }

    // Subscribe to keyboard target changes to update active state display
    // (OnPush components need explicit change detection when service state changes)
    this.keyboardService.targetChanged$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroyGuard.destroy();
    // Make sure to stop keyboard if component is destroyed
    if (this.currentInputControl) {
      void this.keyboardService.stopKeyboard();
      this.currentInputControl = undefined;
    }

    // Notify observers of destruction
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  async ngAfterViewInit() {
    // autofocus must not be used during E2E tests, because it moves focus while E2Es is typing ending in race conditions
    if (this.autofocus && !isE2ETest()) {
      // if mobile, focus the display element; if desktop, focus the input
      if (this.isMobile && this.display1) {
        this.display1.nativeElement.focus();
      } else if (this.input1) {
        this.input1.nativeElement.focus();
      }
    }
  }

  async focus() {
    // if mobile, start the virtual keyboard; if desktop, focus the input
    if (this.isMobile && this.display1) {
      await this.startKeyboardForElement(this.display1.nativeElement);
    } else if (this.input1) {
      this.input1.nativeElement.focus();
    }
  }

  handleText1Input(ev: Event) {
    this.text1 = (ev.target as HTMLInputElement).value;
  }

  handleText2Input(ev: Event) {
    this.text2 = (ev.target as HTMLInputElement).value;
  }

  // Unit column click handler - opens keyboard on mobile, shows popover on desktop
  onUnitColumnClick(event: MouseEvent) {
    if (this.readonly || this.disabled) {
      return;
    }

    event.stopPropagation();

    // on mobile, open the virtual keyboard (targeting primary display)
    if (this.isMobile) {
      const displayElement = this.display1?.nativeElement;
      if (displayElement) {
        void this.startKeyboardForElement(displayElement);
      }
      return;
    }

    // on desktop, show unit popover if multiple units available
    if (this.multipleUnits && !this.lockUnit) {
      void this.presentPopover(event);
    }
  }

  handleButtonClick(event: MouseEvent) {
    if (this.readonly) {
      return;
    }

    const target = event.target as HTMLElement;

    if (this.isMobile) {
      // find which display-value was clicked, or default to primary
      const displayElement = target.closest('.display-value') as HTMLElement;
      void this.startKeyboardForElement(displayElement || this.display1?.nativeElement);
    } else {
      // on desktop, if click was directly on an <input>, let onFocusIn handle it
      // unless keyboard was dismissed and needs re-opening
      if (target.tagName === 'INPUT') {
        if (this.currentInputControl === target) return;
        if (target === document.activeElement && this.useCustomKeyboard() && target instanceof HTMLInputElement) {
          void this.startKeyboardForElement(target);
        }
        return;
      }
      // click on padding/gap area — focus the primary input
      this.input1?.nativeElement.focus();
    }
  }

  /// Remap custom units to use existing descriptions in the global measurement types if applicable
  remapCustomUnitConfig(input: MeasureUnitConfig): MeasureUnitConfig {
    let match = GlobalMeasurementConfigs.find(m => m.id == input.id);
    let description = match ? match.description : input.description;

    return {
      ...input,
      description: description,
    };
  }

  // We need this because angular seems to call ngOnChanges _before_ ngOnInit() in some cases.
  private initIfNeeded() {
    // init only once
    if (this.measurementUnitConfigs) {
      return;
    }

    this.measurementUnitConfigs = [];

    if (this.customUnitConfigs && this.customUnitConfigs.length > 0) {
      this.measurementUnitConfigs = this.customUnitConfigs.map(c => this.remapCustomUnitConfig(c));
    }

    if (this.type) {
      this.measurementType = GlobalMeasurementTypes.find(m => m.id == this.type);
      // if the id is unknown, report it and show a plain number rather than guess a type
      if (!this.measurementType) {
        this.injector.get(LoggingService).trackSilentProblem('MeasureInputUnknownType', { type: this.type });
        this.measurementType = UNKNOWN_MEASUREMENT_TYPE;
      }
      if (this.measurementUnitConfigs.length == 0) {
        this.measurementUnitConfigs = this.measurementUnitConfigs.concat(this.measurementType.configs);
      }
      this.nominalUnit = this.measurementType.nominalUnit;
      this.precision = this.measurementType.precision;
      // if caller overrides precision, create a copy of the type with adjusted precision
      if (this.precisionOverride != null) {
        this.precision = this.precisionOverride;
        this.measurementType = { ...this.measurementType, precision: this.precisionOverride };
      }
    }

    if (this.measurementUnitConfigs.length == 0) {
      this.measurementUnitConfigs = [Config_None];
    }

    // builtin types should initialize these, but custom types don't, so make sure value are safe
    this.nominalUnit = this.nominalUnit || 1;
    this.precision = this.precision || 0.01;

    this.multipleUnits = this.measurementUnitConfigs.length > 1;
    this.maximumChars = this.measurementType && this.measurementType.maximumChars;

    let prefs = this.loadPrefs();
    let unit = this.userUnit || prefs[this.type];
    this.setConfig(unit);

    // If the unit was set from preferences (not from input binding), emit the change
    // This ensures the parent component knows what unit was selected
    const unitChangedFromPrefs = !this.userUnit && prefs[this.type] && this.userUnit_int !== this.userUnit;

    this.userUnit = this.userUnit_int;

    if (unitChangedFromPrefs) {
      this.userUnitChange.emit(this.userUnit);
      this.cdr.markForCheck();
    }

    this.canonValue_int = this.canonValue;
    [this.text1, this.text2] = [this.string1_int, this.string2_int] = this.canonValueToTexts_internal(this.canonValue_int);


    // suggestedPreselectValue is now handled by the keyboard directly

    this.cdr.markForCheck(); // make sure the initial values are set
  }

  ngOnChanges(changes: SimpleChanges) {
    void changes;

    this.initIfNeeded(); // angular seems to call ngOnChanges _before_ ngOnInit() in some cases!

    // The MutationObserver will handle clickable class changes automatically

    // if unit has changed
    if (this.userUnit_int != this.userUnit) {
      this.setConfig(this.userUnit);
      this.userUnit = this.userUnit_int;
      // calculate strings for the change (but still based on internal value!)
      let [string1, string2] = this.canonValueToTexts_internal(this.canonValue_int);
      this.text1 = this.string1_int = string1;
      this.text2 = this.string2_int = string2;
    }

    // if canonical value has changed externally
    if (this.canonValue != this.canonValue_int) {
      // calculate strings for it
      let [string1, string2] = this.canonValueToTexts_internal(this.canonValue);
      // if strings would change
      if (string1 != this.string1_int || string2 != this.string2_int) {
        // apply this change
        this.text1 = this.string1_int = string1;
        this.text2 = this.string2_int = string2;
        this.canonValue_int = this.canonValue;
      }
    }

    // If preselectValues changed and keyboard is open, refresh it
    // Only refresh if the values actually changed (not just array reference)
    if (changes.preselectValues && !changes.preselectValues.firstChange && this.currentInputControl) {
      const prev = changes.preselectValues.previousValue as number[] | undefined;
      const curr = changes.preselectValues.currentValue as number[] | undefined;
      const valuesChanged = !prev || !curr || prev.length !== curr.length ||
        prev.some((v, i) => v !== curr[i]);

      if (valuesChanged) {
        // The preselect values have changed (e.g., equipment changed after unit switch)
        // Restart the keyboard to get the new values
        void this.keyboardService.startKeyboard(
          this.currentInputControl,
          () => this.getKeyboardOptions()
        );
      }
    }

    if (this.readonly) {
      const space = Formatting.thinSpace;
      this.fullText = this.makeBallpark ? '~' : '';
      if (this.unit1name || this.unit2name) {
        if (this.unit1name) {
          this.fullText += this.text1 + space + (this.unit1short || this.unit1name);
        }
        if (this.unit2name) {
          this.fullText += space + this.text2 + space + this.unit2name;
        }
      } else {
        this.fullText += this.text1;
      }
    }
  }

  loadPrefs(): any {
    let prefs: any = null;
    if (this.settings.unitsPrefsJSON) {
      try {
        prefs = JSON.parse(this.settings.unitsPrefsJSON);
      } catch (err) {
        // the person gets the default units, and the next savePrefs overwrites the damaged value.
        // nothing in the app writes a damaged value, so damage is still worth seeing. once per
        // page load: every measure input on every screen reads this setting.
        if (!unitsPrefsDamageReported) {
          unitsPrefsDamageReported = true;
          this.injector.get(LoggingService).trackSilentProblem('MeasureUnitsPrefsDamaged', { detail: describeError(err) });
        }
      }
    }

    return prefs || {};
  }

  savePrefs(prefs: any) {
    this.settings.unitsPrefsJSON = JSON.stringify(prefs);
    this.settings.saveInBackground('unit preferences');
  }

  setConfig(configId: string) {
    this.measurementUnitConfig = findBestUnitConfig(this.measurementUnitConfigs, this.settings.locale, configId);

    this.unit1name = this.measurementUnitConfig.unit1;
    // doNotShortenUnit1: a translated built-in measure name must render whole (see its
    // definition in measure-configs.ts) -- leaving unit1short undefined here is what
    // shortUnitName itself already returns for "nothing to shorten", so the existing
    // `unit1short || unit1name` fallback (fullText above in ngOnChanges, and the template)
    // renders the whole name with no other change needed
    this.unit1short = this.measurementUnitConfig.doNotShortenUnit1 ? undefined : shortUnitName(this.unit1name);
    this.unit2name = this.measurementUnitConfig.unit2;
    this.userUnit_int = this.measurementUnitConfig.id;
    this.fractionDenominatorMax = this.measurementUnitConfig.fractionDenominatorMax;

    // read valueLen from unit config first, fall back to measurement type
    this.valueLen = this.measurementUnitConfig.valueLen || this.measurementType?.valueLen || '';
    this.value2Len = this.measurementUnitConfig.value2Len || '';

    this.cdr.markForCheck(); // make sure the initial values are set
  }

  /*
  Note: On ionChange, we update the value, but we don't do back-correction of strings based on value 
  clamping/rounding. The reason is    that (a) we need to update the value (if valid), to make sure 
  that eventual "Next" button gets enabled before the user presses the Tab key. Yet, (b) we don't want 
  change the text while the user is writing as it's very confusing. (E.g. if minimum is >=10, then when 
  you start typing one digit, it gets clamped up.)
  */
  onTextsChange(forceDetection?: boolean) {
    // if they didn't really change
    if (this.text1 === this.string1_int && this.text2 === this.string2_int && !forceDetection) {
      // do nothing
      return;
    }

    // convert the texts to value
    let val = this.textsToCanonValue_internal(this.text1, this.text2, this.measurementUnitConfig);
    this.string1_int = this.text1;
    this.string2_int = this.text2;

    // clamp value
    if (val != undefined && this.measurementType) {
      val = Math.max(val, this.measurementType.minimum);
      val = Math.min(val, this.measurementType.maximum);
    }

    this.canonValue = this.canonValue_int = val;
    this.canonValueChange.emit(this.canonValue);
    if (this.emitDoneEventOnEveryKeypress) {
      this.editDone.emit(this.canonValue);
    }

    this.cdr.markForCheck();
  }

  /*
  Now when user moves the focus away from the control, we can readjust strings if any clamping/correction happened.
  On desktop: use browser focus management
  On mobile: keyboard lifecycle is managed by internal state, not blur events
  */
  async onTextsBlur(event: Event) {
    void event;

    // On mobile, keyboard lifecycle is managed by internal state (isActiveInput), not browser focus
    // Skip all keyboard stop logic - keyboard is closed explicitly via onClose callback
    if (this.isMobile) {
      // Still do value correction below
    } else {
      // Desktop: use browser focus management
      // Don't stop keyboard if we're selecting a unit from the keyboard
      if (this.isSelectingUnitFromKeyboard) {
        return;
      }

      // Check keyboard stop after DOM update
      if (this.currentInputControl) {
        if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
        // Only check if focus moved within the app, not if app lost focus
        if (document.activeElement) {
          // Check if focus moved to a non-input element
          const activeElement = document.activeElement;
          // Check for INPUT or element inside cs-input-measure
          const isInputElement = activeElement.tagName === 'INPUT' ||
                                 activeElement.closest('cs-input-measure');

          if (!isInputElement) {
            // Focus moved to something that's not an input
            void this.keyboardService.stopKeyboard();
          }
          // If focus moved to another input, keyboard will be updated by that input's focus handler
        }
        // Don't close keyboard if document.activeElement is null (app lost focus)
      }
    }

    try {
      // convert the texts to value
      let val = this.textsToCanonValue_internal(this.text1, this.text2, this.measurementUnitConfig);

      // clamp value
      if (val != undefined && this.measurementType) {
        val = Math.max(val, this.measurementType.minimum);
        val = Math.min(val, this.measurementType.maximum);
      }

      // re-generate the strings
      let [string1, string2] = this.canonValueToTexts_internal(val);
      this.text2 = string2;
      this.text1 = string1;

      // if they didn't really change
      if (this.text1 == this.string1_int && this.text2 == this.string2_int) {
        // do nothing
        return;
      }

      this.string1_int = string1;
      this.string2_int = string2;

      this.canonValue = this.canonValue_int = val;
      this.canonValueChange.emit(this.canonValue);

      /* If user unit was not set externally, we have picked it on init. This makes sure we write that value out, in case there is a binding for it. The effect is that the next time this same number is edited, the same unit will be used.

    This is especially relevant when that unit is synchronized between different objects (e.g. in sets and exercises).
    */
      this.userUnitChange.emit(this.userUnit);

      // also persist the effective unit to global prefs so other screens defaulting for
      // this measurement type pick the same unit. guarded on absence of a pref so an already-
      // established preference cannot be overwritten — a user who has chosen kg globally
      // should not see it flipped by typing into an old lb log entry.
      // skipped when there is no unit picker on this input: custom configs mean per-food
      // overrides that should not leak into global prefs, and a single-unit type (reps, sets,
      // percent, bodyfat_pc, ...) has nothing meaningful to persist. treat an empty custom
      // configs array the same as undefined, matching the customUnitConfigs-length guard in initIfNeeded.
      if (!this.customUnitConfigs?.length && this.multipleUnits && this.userUnit) {
        const prefs = this.loadPrefs();
        if (!prefs[this.type]) {
          prefs[this.type] = this.userUnit;
          this.savePrefs(prefs);
        }
      }
    } finally {
      this.editDone.emit(this.canonValue);
    }
    this.cdr.markForCheck();
  }

  textsToCanonValue_internal(string1: string, string2: string, config: MeasureUnitConfig): number {
    if (string1 === undefined) {
      return undefined;
    }
    // Ensure strings are actually strings
    string1 = String(string1 || '');
    string2 = String(string2 || '');
    
    // first, checks for empty values and NaNs/INFs
    let val1 = parseFraction(string1, this.settings.locale);
    let finite1 = isFinite(val1);
    let val2 = parseFraction(string2, this.settings.locale);
    let finite2 = isFinite(val2);

    // if only one field
    if (config.value2 == undefined) {
      if (!finite1) {
        return undefined;
      }
      // if two fields
    } else {
      if (!finite1 && !finite2) {
        return undefined;
      }
    }

    // this.nominalUnit rather than the measurement type's own: a custom measure
    // has no type to read it from and gets it as an input instead
    return displayValuesToCanonical(val1, val2, config.value1, config.value2, this.nominalUnit);
  }

  // convert from actual number into the text fields
  canonValueToTexts_internal(canonValue: number): string[] {
    let strings = canonValueToTexts_global(canonValue, this.measurementType, this.measurementUnitConfig, this.settings.locale, {
      makeBallpark: this.makeBallpark, group: false,
    });
    // if the selected unit is "none" (can be used for custom measures), then disable the input field because the number cannot be converted
    this.notEditable = this.measurementUnitConfig.value1 == undefined;
    return strings;
  }

  preselectValuesToTexts_internal(preselectValues: number[]): string[] {
    let strings = preselectValues.map(v => canonValueToTexts_global(v, this.measurementType, this.measurementUnitConfig, this.settings.locale, {
      makeBallpark: this.makeBallpark, group: false,
    })[0]);
    return strings;
  }

  public getRollerLabel(): string {
    // Use the rollerLabel from measurement type if specified
    if (this.measurementType?.rollerLabel) {
      return this.measurementType.rollerLabel.toUpperCase();
    }
    // Return undefined if no label specified - the UI will handle not showing it
    return undefined;
  }

  private generateAutomaticRollerValues(): InputValue[] {
    if (!this.measurementType || !this.measurementUnitConfig) {
      return [];
    }

    const min = this.measurementType.minimum;
    const max = this.measurementType.maximum;
    let precision = this.measurementType.precision;

    if (min === undefined || max === undefined || precision === undefined) {
      return [];
    }

    // The precision is meant for the display unit, not the nominal unit
    // We need to convert it to nominal unit scale for generation
    const nominalUnit = this.measurementType.nominalUnit || 1;
    const unitValue = this.measurementUnitConfig.value1 || 1; // How many canonical units is 1 display unit
    
    // Precision is already in display unit scale, use as-is

    // Convert min/max from nominal units to display units
    const minDisplay = min * nominalUnit / unitValue;
    const maxDisplay = max * nominalUnit / unitValue;
    
    // Generate values in display units
    const values: number[] = [];
    for (let value = minDisplay; value <= maxDisplay; value += precision) {
      // Round to precision to avoid floating point issues
      const valueRounded = roundToPrecision(value, precision);
      values.push(valueRounded);
    }
    
    // Ensure we include the maximum value if not already included (due to floating point)
    const lastValue = values[values.length - 1];
    if (lastValue < maxDisplay) {
      values.push(maxDisplay);
    }
    
    // Calculate decimal places for formatting based on precision
    let decimalPlaces = 0;
    if (precision < 1) {
      decimalPlaces = Math.max(0, Math.ceil(-Math.log10(precision)));
    }
    
    // Create InputValue objects with display unit values and formatted labels
    const decimalSeparator = getEffectiveDecimalSeparator(this.settings.locale);
    return values.map(displayValue => {
      return InputValue.fromNumber(displayValue, decimalPlaces, decimalSeparator);
    });
  }

  private generateDualRollerValues(): { values1: InputValue[], values2: InputValue[] } {
    if (!this.measurementType || !this.measurementUnitConfig) {
      return { values1: [], values2: [] };
    }

    // Check if this is a dual unit config (has both unit1 and unit2)
    if (!this.measurementUnitConfig.unit2 || !this.measurementUnitConfig.value2) {
      return { values1: [], values2: [] };
    }

    const min = this.measurementType.minimum;
    const max = this.measurementType.maximum;
    const precision = this.measurementType.precision || 1;

    if (min === undefined || max === undefined) {
      return { values1: [], values2: [] };
    }

    // Get the conversion factors from the config
    const unit1Value = this.measurementUnitConfig.value1; // How many canonical units is 1 unit1
    const unit2Value = this.measurementUnitConfig.value2; // How many canonical units is 1 unit2
    const nominalUnit = this.measurementType.nominalUnit || 1;
    
    // Calculate how many unit2s are in one unit1
    const unit2PerUnit1 = unit1Value / unit2Value;
    
    // Convert min/max from measurement type scale to canonical units first
    const minCanonical = min * nominalUnit;
    const maxCanonical = max * nominalUnit;
    
    // Then convert from canonical units to unit1
    const minUnit1 = Math.floor(minCanonical / unit1Value);
    const maxUnit1 = Math.ceil(maxCanonical / unit1Value);
    
    // Generate unit1 values (always with precision of 1)
    const values1: InputValue[] = [];
    const decimalSeparator = getEffectiveDecimalSeparator(this.settings.locale);
    for (let val = minUnit1; val <= maxUnit1; val++) {
      // Create InputValue with numeric value and string label
      values1.push(InputValue.fromNumber(val, 0, decimalSeparator));
    }
    
    // Generate unit2 values
    // The precision applies to unit2, and it's already in the current unit scale
    // For dual units, precision is for unit2 (e.g., inches can be 0, 1, 2... or 0, 0.5, 1, 1.5...)
    const values2: InputValue[] = [];
    const maxUnit2 = Math.floor(unit2PerUnit1); // Maximum unit2 before rolling over to next unit1
    
    // Calculate decimal places once, outside the loop
    const decimalPlaces = precision < 1 ? Math.max(0, Math.ceil(-Math.log10(precision))) : 0;
    
    for (let val = 0; val < maxUnit2; val += precision) {
      // Round to avoid floating point issues
      const roundedVal = roundToPrecision(val, precision);
      // Create InputValue with numeric value and formatted label
      values2.push(InputValue.fromNumber(roundedVal, decimalPlaces, decimalSeparator));
    }
    
    return { values1, values2 };
  }

  private isSelectingUnitFromKeyboard = false;
  
  public selectUnit(event: Event, fromKeyboard: boolean = false) {
    this.isSelectingUnitFromKeyboard = fromKeyboard;
    void this.presentPopover(event);
  }

  public async presentPopover(event: Event) {
    if (this.readonly) {
      return; // Don't show popover for readonly inputs
    }
    if (event) {
      event.stopPropagation(); // prevents bubbling when control is part of a bigger clickable control
    }

    // Get the entire input-measure component for positioning
    const inputMeasureElement = this.elementRef.nativeElement as HTMLElement;

    let result = await this.popupMenu.createMenu({
      title: (this.customUnitConfigs && this.customUnitConfigs.length > 0) ? LL.common.pickerTitles.servingSize() : LL.common.pickerTitles.selectUnit(),
      buttons: this.measurementUnitConfigs.map(c => ({
        text: untranslatedString(c.doNotShortenUnit1 ? c.unit1 : (shortUnitName(c.unit1) || c.unit1)),
        // localized unit description, shown as the help tooltip / label fallback in the picker
        // (df-b0427dd3fe59). translateMeasureDescription resolves c.descriptionKey to the
        // translated string, falling back to the raw English description if a config has no key.
        description: translateMeasureDescription(c),
        value: c.id,
      })),
      value: this.userUnit,
      targetFromElement: inputMeasureElement,
    });
    this.cdr.markForCheck();

    void this.onPopoverDismiss(result.value);
  }


  async onPopoverDismiss(configId: string) {
    // Reset the flag and refocus if we were selecting from keyboard
    const wasSelectingFromKeyboard = this.isSelectingUnitFromKeyboard;
    this.isSelectingUnitFromKeyboard = false;

    // if user tapped outside of the popover
    if (configId == null) {
      // Refocus the input if we were selecting from keyboard
      if (wasSelectingFromKeyboard && this.currentInputControl) {
        if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
        this.currentInputControl?.focus();
      }
      this.cdr.markForCheck();
      // don't change anything
      return;
    }

    this.userUnit = configId;
    this.setConfig(configId);
    this.userUnit = this.userUnit_int;
    this.userUnitChange.emit(this.userUnit);

    if (!this.unitChangeConvertsValue) {
      this.onTextsChange(true);
      this.cdr.markForCheck();
      return;
    }


    // calculate strings for new unit
    let [string1, string2] = this.canonValueToTexts_internal(this.canonValue);
    // if strings would change
    if (string1 != this.string1_int || string2 != this.string2_int) {
      // apply this change
      this.text1 = this.string1_int = string1;
      this.text2 = this.string2_int = string2;
    }

    // Snap values to preselect values if within precision
    this.snapTextsToPreselectValue();

    // treat an empty custom configs array the same as undefined, matching the customUnitConfigs-length guard in initIfNeeded
    if (!this.customUnitConfigs?.length) {
      let prefs = this.loadPrefs();
      prefs[this.type] = configId;
      this.savePrefs(prefs);
    }

    // Refocus the element if we were selecting from keyboard
    if (wasSelectingFromKeyboard && this.currentInputControl) {
      if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
      this.currentInputControl?.focus();
      // Restart the keyboard since we had to blur temporarily
      await this.startKeyboardForElement(this.currentInputControl);
    }
    
    this.cdr.markForCheck();
  }

  // After a unit change, pull the displayed text onto a preselect value when what is shown is
  // already within one precision step of it. Without this, switching units lands on the
  // converted neighbour of a suggested number (22.7) rather than the suggestion itself (22.5).
  //
  // The comparison is made on the DISPLAYED text, not on the canonical value, because the text
  // has already been rounded to the type's precision -- so comparing what is on screen is what
  // makes "close enough" mean what the user can see.
  private snapTextsToPreselectValue(): void {
    if (!this.preselectValues || !this.text1) {
      return;
    }

    // A dual-unit config (stones and pounds, feet and inches) splits ONE value over two fields,
    // so it is compared and snapped as a whole, in units of the second field. Matching or
    // snapping half of it on its own would build a number the user never entered -- 10 st 5 lb
    // becoming 10 st 6 lb because the pounds alone happened to sit near a preselect value.
    const unitRatio = dualUnitRatio(this.measurementUnitConfig);
    const isDualUnit = !!this.text2 && unitRatio != undefined;
    const displayedValue = isDualUnit
      ? this.parseDisplayedNumber(this.text1) * unitRatio + this.parseDisplayedNumber(this.text2)
      : this.parseDisplayedNumber(this.text1);

    for (const preselectValue of this.preselectValues) {
      const preselectTexts = canonValueToTexts_global(preselectValue, this.measurementType, this.measurementUnitConfig, this.settings.locale, {
        makeBallpark: this.makeBallpark, group: false,
      });
      const preselectNumber = isDualUnit
        ? this.parseDisplayedNumber(preselectTexts[0]) * unitRatio + this.parseDisplayedNumber(preselectTexts[1])
        : this.parseDisplayedNumber(preselectTexts[0]);
      const diff = roundToPrecision(Math.abs(preselectNumber - displayedValue), this.measurementType.precision);
      if (diff <= this.measurementType.precision) {
        this.text1 = preselectTexts[0];
        if (isDualUnit) {
          this.text2 = preselectTexts[1];
        }
        this.cdr.markForCheck();
        break;
      }
    }
  }

  // read a number back out of a displayed text field, matching how the other call sites in this
  // file do it: the only separator the fields ever show is a comma or a dot
  private parseDisplayedNumber(text: string): number {
    return parseFloat(text.replace(/,/, '.'));
  }

  isTouchDevice() {
    return ('ontouchstart' in document.documentElement);
  }

  onTextsClick(input: FocusEvent) {

    input.stopPropagation(); // prevents bubbling when control is part of a bigger clickable control
    input.preventDefault();
    input.stopImmediatePropagation();

    if (this.readonly) {
      return; // Don't process clicks on readonly inputs
    }

    // Skip if click was on a unit column (they have their own click handlers)
    const target = input.target as HTMLElement;
    if (target && target.closest('.unit-column')) {
      return;
    }
    // if this is a custom unit and the user has not selected a unit yet, but could select it
    if (this.measurementUnitConfig.value1 == undefined && this.measurementUnitConfigs.length > 1) {
      // show the unit popup now
      void this.presentPopover(input);
      return;
    }

    // Let onFocusIn handle keyboard opening with roller support
    // The getKeyboardOptions method will include roller options if needed
  }


  useCustomKeyboard() {
    return this.isTouchDevice();
  }

  public useInputScroller() {
    return this.isTouchDevice();
  }

  // Extract common roller value generation logic
  private generateRollerValues(
    preselectValues: number[],
    unitScale: number,
    precision: number
  ): {
    values: InputValue[];
    suggested: number;
    current: number | undefined;
  } {
    const preselectInDisplayUnits = preselectValues.map(v => roundToPrecision(v / unitScale, precision));

    // Calculate decimal places for formatting based on precision
    let decimalPlaces = 0;
    if (precision < 1) {
      decimalPlaces = Math.max(0, Math.ceil(-Math.log10(precision)));
    }

    // Find the lowest significant decimal place in all of the preselect values
    let furthestDecimal = 0;
    for (const value of preselectInDisplayUnits) {
      const strValue = value.toString();
      const decimalIndex = strValue.indexOf('.');
      if (decimalIndex !== -1) {
        const currentDecimalPlaces = strValue.length - decimalIndex - 1;
        furthestDecimal = Math.max(furthestDecimal, currentDecimalPlaces);
      }
    }

    // Use the lowest decimal places found, but not more than what precision allows
    decimalPlaces = Math.min(decimalPlaces, furthestDecimal);

    // Create InputValue objects with simple numeric formatting
    const decimalSeparator = getEffectiveDecimalSeparator(this.settings.locale);
    const rollerValues = preselectInDisplayUnits.map((value) => {
      return InputValue.fromNumber(value, decimalPlaces, decimalSeparator);
    });

    const suggestedNumericValue = (this.suggestedPreselectValue || preselectValues[0]) / unitScale;
    const currentValue = parseFraction(this.text1, this.settings.locale);

    return {
      values: rollerValues,
      suggested: suggestedNumericValue,
      current: isFinite(currentValue) ? currentValue : undefined
    };
  }
  
  // Generate single roller options without considering pairing
  private generateSingleRollerOptions(): Partial<KeyboardOptions> {
    const options: Partial<KeyboardOptions> = {};

    // Add roller options if we should show roller
    if (this.measurementType?.automaticRoller && this.useInputScroller() && !this.forceKeypadInput && this.measurementUnitConfig?.unit2) {
      // Dual unit configs like feet/inches - handled in the main method
      // Skip here to avoid duplication
      return options;
    } else if (this.preselectValues && this.useInputScroller() && !this.forceKeypadInput) {
      // Single roller with preselect values
      const nominalUnit = this.measurementType?.nominalUnit || 1;
      const unitValue = this.measurementUnitConfig?.value1 || 1;
      const unitScale = unitValue / nominalUnit;
      const precision = this?.measurementType?.precision || 1;

      const rollerData = this.generateRollerValues(this.preselectValues, unitScale, precision);

      Object.assign(options, {
        preselectValuesNumeric1: rollerData.values,
        suggestedValueNumeric1: rollerData.suggested,
        currentValueNumeric1: rollerData.current,
        startInRollerMode: true,
        rollerLabel1: this.getRollerLabel()
      });
    }

    return options;
  }
  
  // Public method to generate roller options that can be called by paired components
  public generateRollerOptionsForPairing(): Partial<KeyboardOptions> {
    const options: Partial<KeyboardOptions> = {};

    // Always generate roller values if we have preselect values, regardless of other conditions
    // This ensures paired components can always provide their values
    if (this.preselectValues) {
      const nominalUnit = this.measurementType?.nominalUnit || 1;
      const unitValue = this.measurementUnitConfig?.value1 || 1;
      const unitScale = unitValue / nominalUnit;
      const precision = this?.measurementType?.precision || 1;

      const rollerData = this.generateRollerValues(this.preselectValues, unitScale, precision);

      options.preselectValuesNumeric1 = rollerData.values;
      options.suggestedValueNumeric1 = rollerData.suggested;
      options.currentValueNumeric1 = rollerData.current;
      options.rollerLabel1 = this.getRollerLabel();
    }

    // Include unit configuration if this component has units
    if (this.measurementUnitConfigs && this.measurementUnitConfigs.length > 0) {
      options.unitConfigs = this.measurementUnitConfigs;
      options.currentUnitId = this.measurementUnitConfig?.id || this.userUnit;
    }

    return options;
  }

  // Build everything the pop-up keyboard needs in order to edit this input.
  //
  // The keyboard can appear in one of several shapes, and this picks exactly one
  // of them: two wheels driven by a partner input, two wheels for a unit written
  // as two numbers (5 ft 9 in), one wheel of suggested values, one wheel built
  // from the allowed range, or a plain number pad. The settings common to every
  // shape are built here; each shape's own settings come from its own builder
  // below.
  private getKeyboardOptions(): KeyboardOptions {
    const options: KeyboardOptions = this.buildSharedKeyboardOptions();

    // Check if we're part of a pair and should use dual roller
    const pairOptions = this.pairingService.getKeyboardOptionsForPair(this);
    if (pairOptions) {
      // Merge pair options with base options
      Object.assign(options, pairOptions);
      // Add navigation hint for the keyboard
      options.shouldSkipNextInSequence = this.isDualRollerActive() && this.pairingService.getRole(this) === 'primary';
      // Return early to prevent single roller setup
      return options;
    }

    // Pick the one shape this input wants. The order matters: a unit written as
    // two numbers takes the two-wheel shape even when suggested values exist,
    // because one wheel cannot express "5 ft 9 in".
    if (this.measurementType?.automaticRoller && this.useInputScroller() && !this.forceKeypadInput && this.measurementUnitConfig?.unit2) {
      Object.assign(options, this.buildDualUnitRollerOptions());
    } else if (this.preselectValues && this.useInputScroller() && !this.forceKeypadInput) {
      Object.assign(options, this.buildPreselectRollerOptions());
    } else if (this.measurementType?.automaticRoller && this.useInputScroller() && !this.forceKeypadInput) {
      Object.assign(options, this.buildAutomaticRollerOptions());
    } else {
      Object.assign(options, this.buildKeypadOptions());
    }

    return options;
  }

  // The settings every shape of the keyboard gets: which keys it may show, which
  // unit it is editing in, which element it is attached to, and what to do as the
  // user types or when the keyboard closes.
  private buildSharedKeyboardOptions(): KeyboardOptions {
    // No longer need to pre-format suggested value - keyboard handles display

    // Ensure we have a valid element to pass to the keyboard
    // if currentInputControl is undefined or disconnected, use the primary element
    let targetElement = this.currentInputControl;
    if (!targetElement || !targetElement.isConnected) {
      // Default to the primary element (display1 on mobile, input1 on desktop)
      targetElement = this.isMobile ? this.display1?.nativeElement : this.input1?.nativeElement;
      // Update currentInputControl to match
      if (targetElement) {
        this.currentInputControl = targetElement;
      }
    }
    
    // Build keyboard options
    // Components without units shouldn't show unit controls
    const hasUnits = this.multipleUnits || this.measurementUnitConfig?.alwaysShowUnit;
    // Determine initial display text based on which element is active
    const isPrimaryElement = targetElement === this.display1?.nativeElement ||
                            targetElement === this.input1?.nativeElement;
    const initialText = isPrimaryElement ? this.text1 : this.text2;
    return {
      allowFractions: this.measurementUnitConfig?.fractionDenominatorMax != null,
      allowUnits: hasUnits,
      allowScroller: this.useInputScroller(),
      allowDecimal: this.measurementType?.precision < 1,
      decimalSeparator: getEffectiveDecimalSeparator(this.settings.locale),
      initialDisplayText: initialText || '', // Pass current text value for mobile divs
      unitConfigs: hasUnits ? (this.measurementUnitConfigs || []) : [],
      currentUnitId: this.measurementUnitConfig?.id || this.userUnit,
      alwaysShowUnit: this.measurementUnitConfig?.alwaysShowUnit || false,
      onUnitChange: (unitId: string) => this.onKeyboardUnitChange(unitId),
      isCustomUnitConfig: this.customUnitConfigs && this.customUnitConfigs.length > 0,
      targetElement: targetElement, // Pass the validated input element
      // Real-time text updates as user types (for keyboard mode)
      onTextChange: (text: string) => {
        // Update the appropriate field with the raw text
        // This allows incomplete values like "3." or "1 3/"
        // Check for primary element (input1 on desktop, display1 on mobile)
        const isPrimary = this.currentInputControl === this.input1?.nativeElement ||
                         this.currentInputControl === this.display1?.nativeElement;
        if (isPrimary) {
          this.text1 = text;
        } else {
          // if it's not primary, it must be secondary (for dual unit configs)
          this.text2 = text;
        }
        this.onTextsChange();
      },
      onClose: () => {
        this.currentInputControl = undefined;
        this.editDone.emit(this.canonValue);
        this.cdr.markForCheck();
      },
    };
  }

  // Turn the numbers the keyboard reports into canonical units, using this
  // input's current unit config. The keyboard always reports display units, so
  // this runs before anything is formatted or stored.
  //
  // Used by the shapes that can carry a second number -- the two-wheel picker
  // and the number pad. The two single-wheel shapes do their own conversion,
  // because they group the same multiplication and division differently and a
  // shared version would round differently from what they do today.
  private rollerValuesToCanonical(values: { value1: number; value2?: number }): number {
    return displayValuesToCanonical(
      values.value1,
      values.value2,
      this.measurementUnitConfig?.value1 || 1,
      this.measurementUnitConfig?.value2,
      this.measurementType?.nominalUnit || 1,
    );
  }

  // Two wheels for a unit that is written as two numbers, like 5 ft 9 in or
  // 13 st 4 lb. One wheel could not express such a value, so this shape wins
  // even when the input also has a list of suggested values.
  private buildDualUnitRollerOptions(): Partial<KeyboardOptions> {
    // Generate dual roller values for units like feet/inches
    const dualValues = this.generateDualRollerValues();
    if (dualValues.values1.length === 0 || dualValues.values2.length === 0) {
      return {};
    }

    // Use current values as suggested values, or defaults if not set
    let suggestedNumeric1: number;
    let suggestedNumeric2: number;
    let currentNumeric1: number = NaN;
    let currentNumeric2: number = NaN;

    if (this.text1) {
      // Parse current text values to numbers
      currentNumeric1 = parseFloat(this.text1.replace(/,/, '.'));
      currentNumeric2 = parseFloat((this.text2 || '0').replace(/,/, '.'));
      suggestedNumeric1 = isFinite(currentNumeric1) ? currentNumeric1 : dualValues.values1[0].numeric;
      suggestedNumeric2 = isFinite(currentNumeric2) ? currentNumeric2 : 0;
    } else if (this.measurementType.defaultValue !== undefined) {
      // Convert default value to current units
      const defaultTexts = this.canonValueToTexts_internal(this.measurementType.defaultValue);
      // ALWAYS round initial default values to whole numbers for clean initial selection
      suggestedNumeric1 = Math.round(parseFloat(defaultTexts[0].replace(/,/, '.')));
      suggestedNumeric2 = Math.round(parseFloat((defaultTexts[1] || '0').replace(/,/, '.')));
    } else {
      // Use minimum values as fallback
      suggestedNumeric1 = dualValues.values1[0].numeric;
      suggestedNumeric2 = 0;
    }

    // a wheel can only stop on a value it was given
    suggestedNumeric1 = closestValueTo(dualValues.values1, suggestedNumeric1).numeric;
    suggestedNumeric2 = closestValueTo(dualValues.values2, suggestedNumeric2).numeric;

    // Convert the two display-unit values (unit1 and unit2) to canonical,
    // format, and set the visible texts. Shared by the per-tick preview
    // (text only) and the settle-time commit (text + editDone) (#4042)
    const applyDualUnitValues = (values: { value1: number; value2?: number }) => {
      // Use canonValueToTexts_internal for proper formatting
      const texts = this.canonValueToTexts_internal(this.rollerValuesToCanonical(values));
      this.text1 = texts[0];
      this.text2 = texts[1] || '';
      this.onTextsChange();
    };

    return {
      isDualRoller: true,
      // Pass InputValue arrays directly
      preselectValuesNumeric1: dualValues.values1,
      preselectValuesNumeric2: dualValues.values2,
      suggestedValueNumeric1: suggestedNumeric1,
      suggestedValueNumeric2: suggestedNumeric2,
      currentValueNumeric1: isFinite(currentNumeric1) ? currentNumeric1 : undefined,
      currentValueNumeric2: isFinite(currentNumeric2) ? currentNumeric2 : undefined,
      startInRollerMode: true,
      rollerLabel1: this.measurementUnitConfig.unit1.toUpperCase(),
      rollerLabel2: this.measurementUnitConfig.unit2.toUpperCase(),
      // Per-tick preview: text only, no editDone (#4042)
      onValuePreviewNumeric: (values: { value1: number; value2?: number }) => {
        applyDualUnitValues(values);
      },
      // Use numeric callback only
      onValueSelectedNumeric: (values: { value1: number; value2?: number }) => {
        applyDualUnitValues(values);
        this.editDone.emit(this.canonValue);
      }
    };
  }

  // One wheel offering the short list of values this input was handed -- the
  // weights or rep counts a set can plausibly take, for instance.
  private buildPreselectRollerOptions(): Partial<KeyboardOptions> {
    // Format the selected display-unit value and set the visible text.
    // Shared by the per-tick preview (text only) and the settle-time
    // commit (text + editDone) (#4042)
    const applyPreselectRollerValue = (values: { value1: number; value2?: number }) => {
      // For single roller, format the selected value. The wheel offers one number
      // only, so the second field plays no part and is cleared.
      const nominalUnit = this.measurementType.nominalUnit || 1;
      const unitValue = this.measurementUnitConfig.value1 || 1;
      const canonicalValue = values.value1 * (unitValue / nominalUnit);
      // Find if this matches a preselect value
      const matchingIndex = this.preselectValues.findIndex(v => Math.abs(v - canonicalValue) < 0.001);
      if (matchingIndex >= 0) {
        // Format using the canonical value conversion
        this.text1 = canonValueToTexts_global(canonicalValue, this.measurementType, this.measurementUnitConfig, this.settings.locale, {
          makeBallpark: this.makeBallpark, group: false,
        })[0];
      } else {
        // Use canonValueToTexts_internal for proper formatting
        this.text1 = this.canonValueToTexts_internal(canonicalValue)[0];
      }
      this.text2 = '';
      this.onTextsChange();
    };

    return {
      ...this.generateSingleRollerOptions(),
      // Per-tick preview: text only, no editDone (#4042)
      onValuePreviewNumeric: (values: { value1: number; value2?: number }) => {
        applyPreselectRollerValue(values);
      },
      onValueSelectedNumeric: (values: { value1: number; value2?: number }) => {
        applyPreselectRollerValue(values);
        this.editDone.emit(this.canonValue);
      }
    };
  }

  // One wheel built from the measurement type's own allowed range and step, for
  // an input with no list of suggested values -- a body weight or a height.
  //
  // Unlike the other three builders, this one is NOT free of side effects: it
  // also writes the value the wheel will start on into the input's visible
  // fields, so an empty field shows that value before the user touches the
  // wheel. Anything that reorders or short-circuits the mode selection in
  // getKeyboardOptions loses that write.
  private buildAutomaticRollerOptions(): Partial<KeyboardOptions> {
    // Generate single automatic roller values based on min/max/precision
    const generatedValues = this.generateAutomaticRollerValues();
    if (generatedValues.length === 0) {
      return {};
    }

    const nominalUnit = this.measurementType.nominalUnit || 1;
    const unitValue = this.measurementUnitConfig.value1 || 1;

    // Determine suggested numeric value
    let suggestedNumeric: number;
    let currentNumeric: number = NaN;

    if (this.text1) {
      // Parse current value (already in display units)
      const currentDisplay = parseFloat(this.text1.replace(/,/, '.'));
      if (isFinite(currentDisplay)) {
        currentNumeric = currentDisplay;
        suggestedNumeric = closestValueTo(generatedValues, currentNumeric).numeric;
      } else {
        suggestedNumeric = generatedValues[0].numeric;
      }
    } else if (this.suggestedPreselectValue !== undefined) {
      // Use suggested preselect value (already in canonical units)
      const suggestedDisplay = this.suggestedPreselectValue * nominalUnit / unitValue;
      suggestedNumeric = closestValueTo(generatedValues, suggestedDisplay).numeric;
    } else if (this.measurementType.defaultValue !== undefined) {
      // Convert default value to display units
      const defaultDisplay = this.measurementType.defaultValue * nominalUnit / unitValue;
      suggestedNumeric = closestValueTo(generatedValues, defaultDisplay).numeric;
    } else {
      // Use minimum value as fallback
      suggestedNumeric = generatedValues[0].numeric;
    }

    // show the value the wheel is about to start on straight away
    const texts = this.canonValueToTexts_internal((suggestedNumeric * unitValue) / nominalUnit);
    this.text1 = texts[0];
    this.text2 = texts[1] || '';
    this.onTextsChange();

    // Convert the display-unit value to canonical, format, and set the
    // visible texts. Shared by the per-tick preview (text only) and the
    // settle-time commit (text + editDone) (#4042)
    const applyAutomaticRollerValue = (values: { value1: number; value2?: number }) => {
      // Use canonValueToTexts_internal for proper formatting
      const applied = this.canonValueToTexts_internal((values.value1 * unitValue) / nominalUnit);
      this.text1 = applied[0];
      this.text2 = applied[1] || '';
      this.onTextsChange();
    };

    return {
      // Pass InputValue arrays directly
      preselectValuesNumeric1: generatedValues,
      suggestedValueNumeric1: suggestedNumeric,
      currentValueNumeric1: isFinite(currentNumeric) ? currentNumeric : undefined,
      startInRollerMode: true,
      rollerLabel1: this.getRollerLabel(),
      // Per-tick preview: text only, no editDone (#4042)
      onValuePreviewNumeric: (values: { value1: number; value2?: number }) => {
        applyAutomaticRollerValue(values);
      },
      // Use numeric callback
      onValueSelectedNumeric: (values: { value1: number; value2?: number }) => {
        applyAutomaticRollerValue(values);
        this.editDone.emit(this.canonValue);
      }
    };
  }

  // A plain number pad, for an input that wants no wheel at all. There is no
  // per-tick preview here: the user types, and nothing is stored until they
  // press Accept.
  private buildKeypadOptions(): Partial<KeyboardOptions> {
    // Keyboard mode (not roller) - add numeric callback for Accept button
    // Also pass current values so keyboard can display them
    // Use parseFraction to handle fractions and locale-specific decimal separators
    const currentValue1 = parseFraction(this.text1, this.settings.locale);
    const currentValue2 = this.text2 ? parseFraction(this.text2, this.settings.locale) : undefined;

    return {
      // Pass current values to keyboard
      currentValueNumeric1: isFinite(currentValue1) ? currentValue1 : undefined,
      currentValueNumeric2: currentValue2 !== undefined && isFinite(currentValue2) ? currentValue2 : undefined,
      // Final value when Accept is pressed
      onValueSelectedNumeric: (values: { value1: number; value2?: number }) => {
        // Values from keyboard are in display units
        // For keypad entry (not roller), just set the text directly
        // The user has typed exact values they want
        if (!isFinite(values.value1)) {
          this.text1 = '';
        } else {
          // Still use canonValueToTexts to ensure proper formatting
          const texts = this.canonValueToTexts_internal(this.rollerValuesToCanonical(values));
          this.text1 = texts[0];
          this.text2 = texts[1] || '';
        }

        this.onTextsChange();
        this.editDone.emit(this.canonValue);
      }
    };
  }

  async onFocusIn(event: FocusEvent) {
    // Don't handle focus for readonly inputs
    if (this.readonly) {
      return;
    }

    // No delegation - let each input handle its own focus
    // The pairing logic in getKeyboardOptions will handle showing dual roller when appropriate

    if (this.useCustomKeyboard()) {
      const target = event?.target as HTMLElement;
      // if input element (desktop) or display-value div (mobile)
      if (target && (target instanceof HTMLInputElement || target.classList?.contains('display-value'))) {
        // Use single entry point for starting keyboard
        await this.startKeyboardForElement(target);
      } else {
        this.currentInputControl = undefined;
      }
    }

    // if there is no virtual keyboard, we need to call this directly here, otherwise we will call this on KeyCode.KeyboardAppeared
    if (!this.currentInputControl) {
      await this.onFocusInComplete();
    }
  }

  async onFocusInComplete() {
    // Method kept empty for compatibility
  }

  // Single entry point for starting the keyboard
  private keyboardStartPending = false;
  private async startKeyboardForElement(element: HTMLElement) {
    console.log('[cs-input-measure] startKeyboardForElement', { type: this.type, pending: this.keyboardStartPending });

    if (!this.useCustomKeyboard() || !element || this.readonly) {
      return;
    }

    // Prevent duplicate simultaneous calls (race between click handlers)
    if (this.keyboardStartPending) {
      console.log('[cs-input-measure] blocked - already pending');
      return;
    }

    this.keyboardStartPending = true;
    this.currentInputControl = element;
    this.cdr.markForCheck();
    try {
      await this.keyboardService.startKeyboard(
        this.currentInputControl,
        () => this.getKeyboardOptions()
      );
    } finally {
      this.keyboardStartPending = false;
    }
  }

  // Handle unit change from virtual keyboard
  async onKeyboardUnitChange(unitId: string) {
    // Find the config with this unit ID
    const config = this.measurementUnitConfigs?.find(c => c.id === unitId);
    if (config) {
      // First, update the canonical value based on current input text
      // This ensures we convert the currently displayed/scrolled value, not the original
      // (only needed for desktop input elements that have .value property)
      if (this.currentInputControl && this.currentInputControl instanceof HTMLInputElement && this.currentInputControl.value) {
        // Determine which text to update based on which input is focused
        const isPrimary = this.currentInputControl === this.input1?.nativeElement;
        if (isPrimary) {
          this.text1 = this.currentInputControl.value;
        } else {
          // it's the second input (unit2)
          this.text2 = this.currentInputControl.value;
        }
        this.onTextsChange();
      }

      // Store whether we're switching from dual to single unit
      // Check for primary element (input1 on desktop, display1 on mobile)
      const isPrimaryFocused = this.currentInputControl === this.input1?.nativeElement ||
                              this.currentInputControl === this.display1?.nativeElement;
      const wasSecondaryFocused = this.currentInputControl && !isPrimaryFocused;
      const oldConfig = this.measurementUnitConfig;
      const switchingFromDualToSingle = oldConfig?.unit2 && !config.unit2;

      // Update both the internal and external unit tracking
      this.userUnit_int = unitId;
      this.userUnit = unitId;

      // Emit the change for two-way binding and wait for parent to process
      this.userUnitChange.emit(this.userUnit);

      // Give the parent component time to update (e.g., change equipment)
      // This is important for workout-detail which needs to update equipment
      // and regenerate possibleWeightValues_kg before keyboard refreshes
      if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

      // Change to the selected unit
      this.setConfig(unitId);

      // Save the preference
      let prefs = this.loadPrefs();
      prefs[this.type] = unitId;
      this.savePrefs(prefs);

      // Recalculate the display values with the new unit (now using updated canonValue_int)
      const [string1, string2] = this.canonValueToTexts_internal(this.canonValue_int);
      this.text1 = this.string1_int = string1;
      this.text2 = this.string2_int = string2;

      // Regenerate preselect values for the new unit
      if (this.preselectValues) {
        } else if (this.measurementType?.automaticRoller && this.useInputScroller() && !this.forceKeypadInput) {
        // if using automatic roller, regenerate values for the new unit
        // The virtual keyboard will request fresh options after unit change
        // and generateAutomaticRollerValues will be called with the new unit config
      }

      // suggestedPreselectValue is now handled by the keyboard directly

      // Trigger change detection and wait for DOM to update
      this.cdr.markForCheck();
      await animationFrame();

      // After DOM update, handle the input control reference
      if (switchingFromDualToSingle && wasSecondaryFocused) {
        // The secondary element no longer exists, switch to primary
        // Use display1 on mobile, input1 on desktop
        this.currentInputControl = this.isMobile ? this.display1?.nativeElement : this.input1?.nativeElement;
      }

      // Update the current input display with new value (only for desktop input elements)
      if (this.currentInputControl && this.currentInputControl instanceof HTMLInputElement) {
        const isPrimary = this.currentInputControl === this.input1?.nativeElement;
        if (isPrimary) {
          this.currentInputControl.value = this.text1;
        }
      }
    }
  }

  // DEPRECATED: No longer used - keyboard handles its own input internally
  // and sends complete values via onValueSelectedNumeric callback
  async onKeyPressed(keyCode: KeyCode, ev: Event) {
    void keyCode;
    void ev;
    // This method is kept for backwards compatibility but is no longer called
  }


  async onTextsKeyDown(event: KeyboardEvent) {
    if (event.key == 'Enter' || event.key == 'Tab') {
      event.preventDefault(); // specifically for Tab key, otherwise it will move to the next element after the one we want

      this.onTextsChange();
      if (this.elementRef.nativeElement.hasAttribute('input-sequence')) {
        this.activateNextInSequence();
      }
    }
  }

  async ngAfterViewChecked() {
  }

  // Check if dual roller is currently being shown
  private isDualRollerActive(): boolean {
    // Check if we're paired and both components support roller mode
    const paired = this.pairingService.getPairedComponent(this);
    if (!paired) return false;

    // Both must support roller mode and not be forced to keypad
    return !!(this.useInputScroller() &&
             paired.useInputScroller() &&
             !this.forceKeypadInput &&
             !paired.forceKeypadInput &&
             this.preselectValues &&
             paired.preselectValues);
  }

  // Navigate to next element in sequence - used for desktop Tab key
  activateNextInSequence() {
    let nextElement = findNextVisibleSequenceElement(this.elementRef.nativeElement);

    // Only skip paired element if we're primary AND dual roller is active
    const role = this.pairingService.getRole(this);
    if (role === 'primary' && nextElement && this.isDualRollerActive()) {
      const paired = this.pairingService.getPairedComponent(this);
      if (paired && nextElement === paired.elementRef.nativeElement) {
        // Skip to element after the pair when dual roller is active
        nextElement = findNextVisibleSequenceElement(nextElement);
      }
    }

    if (nextElement) {
      // if the next element is a cs-input-measure, use the input or display element inside it
      let elementToActivate = nextElement;
      if (nextElement.tagName == 'CS-INPUT-MEASURE') {
        // Try input first (desktop), then display-value (mobile)
        elementToActivate = nextElement.querySelector('input') || nextElement.querySelector('.display-value');
      }
      // focus, or click, that found element
      if (elementToActivate.tagName == 'CS-BUTTON') {
        elementToActivate.click();
      } else {
        elementToActivate.focus();
      }
    }
  }

}
